import React, {Component} from 'react';
import I18n from 'app/i18n';
import {isGuest} from 'app/utils';
import {CasinoFilterInterface, CasinoGameInterface} from 'app/interfaces/stores/CasinoGamesStoreInterfaces';
import {size190x190} from 'app/utils/CasinoGameImages';
import {SignInButton} from 'app/components/auth/SignInButton';
import {captureCasinoGameLaunchRow, startDemo, startGame} from 'app/services/CasinoServices';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {showRestrictedCountriesPopup} from 'app/components/pages/casino/RestrictedCountriesPopup';

const POPULAR_SLOTS_LIMIT = 14;
const AUTO_ROTATE_INTERVAL_MS = 5000;

interface PopularGamesCarouselProps {
    filter?: CasinoFilterInterface
    activeClickGame: number
    setActiveGame: (id: number) => void
    deactivateGame: () => void
    cool_off_present?: boolean
}

interface PopularGamesCarouselState {
    activeIndex: number
}

export default class PopularGamesCarousel extends Component<
    PopularGamesCarouselProps,
    PopularGamesCarouselState
> {
    private autoRotateTimerId: ReturnType<typeof setInterval> | null = null;

    constructor(props: PopularGamesCarouselProps) {
        super(props);

        this.state = {
            activeIndex: 0
        };
    }

    componentDidMount(): void {
        this.startAutoRotate();
    }

    componentWillUnmount(): void {
        this.stopAutoRotate();
    }

    componentDidUpdate(prevProps: PopularGamesCarouselProps): void {
        if (prevProps.filter !== this.props.filter) {
            this.setState({activeIndex: 0});
            this.restartAutoRotate();
        }
    }

    get games(): CasinoGameInterface[] {
        const {filter} = this.props;
        return filter?.casinoGames?.slice(0, POPULAR_SLOTS_LIMIT) ?? [];
    }

    startAutoRotate = (): void => {
        this.stopAutoRotate();
        this.autoRotateTimerId = setInterval(this.goToNext, AUTO_ROTATE_INTERVAL_MS);
    };

    stopAutoRotate = (): void => {
        if (this.autoRotateTimerId !== null) {
            clearInterval(this.autoRotateTimerId);
            this.autoRotateTimerId = null;
        }
    };

    restartAutoRotate = (): void => {
        this.startAutoRotate();
    };

    goToNext = (): void => {
        const {games} = this;

        if (games.length === 0) {
            return;
        }

        this.setState(prevState => ({
            activeIndex: (prevState.activeIndex + 1) % games.length
        }));
    };

    handleNextClick = (): void => {
        this.goToNext();
        this.restartAutoRotate();
    };

    goToPrev = (): void => {
        const {games} = this;

        if (games.length === 0) {
            return;
        }

        this.setState(prevState => ({
            activeIndex: prevState.activeIndex === 0
                ? games.length - 1
                : prevState.activeIndex - 1
        }));
        this.restartAutoRotate();
    };

    handleMiniatureClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        const index = Number(e.currentTarget.dataset.index);

        if (!Number.isFinite(index)) {
            return;
        }

        this.goToIndex(index);
    };

    goToIndex = (index: number): void => {
        const {games} = this;

        if (index < 0 || index >= games.length) {
            return;
        }

        if (index === this.state.activeIndex) {
            return;
        }

        this.setState({activeIndex: index});
        this.restartAutoRotate();
    };

    openRestrictedPopup = (): void => {
        const game = this.games[this.state.activeIndex];

        if (!game) {
            return;
        }

        showRestrictedCountriesPopup(game.name, game.restricted_country_codes);
    };

    handleStartGame = (): void => {
        const {filter, setActiveGame} = this.props;
        const game = this.games[this.state.activeIndex];

        if (!game || game.restricted) {
            return;
        }

        setActiveGame(game.id);
        captureCasinoGameLaunchRow(null);
        startGame(game.id, filter);
    };

    handleStartDemo = (): void => {
        const {filter, setActiveGame} = this.props;
        const game = this.games[this.state.activeIndex];

        if (!game || game.restricted) {
            return;
        }

        setActiveGame(game.id);
        captureCasinoGameLaunchRow(null);
        startDemo(game.id, filter);
    };

    renderProgressSegments = (): React.ReactNode[] => {
        const {games} = this;
        const {activeIndex} = this.state;

        return games.map((_, index) => {
            const isActive = index <= activeIndex;
            const isCurrent = index === activeIndex;
            const segmentClass = [
                'popular-games-carousel__progress-segment',
                isActive && 'popular-games-carousel__progress-segment_active',
                isCurrent && 'popular-games-carousel__progress-segment_current'
            ].filter(Boolean).join(' ');

            return (
                <span
                    key={isCurrent ? `current-${activeIndex}` : index}
                    className={segmentClass}
                />
            );
        });
    };

    renderMiniatures = (): React.ReactNode[] => {
        const {games} = this;
        const {activeIndex} = this.state;

        return games.map((miniGame, index) => {
            const miniatureClass = [
                'popular-games-carousel__miniature',
                index === activeIndex && 'popular-games-carousel__miniature_focused',
                miniGame.restricted && 'popular-games-carousel__miniature_restricted'
            ].filter(Boolean).join(' ');

            return (
                <button
                    key={miniGame.id}
                    type="button"
                    className={miniatureClass}
                    data-index={index}
                    onClick={this.handleMiniatureClick}
                    aria-label={miniGame.name}
                    aria-current={index === activeIndex ? 'true' : null}
                >
                    <img
                        alt={miniGame.identifier2}
                        className="popular-games-carousel__miniature-image"
                        src={size190x190(
                            miniGame.provider_name,
                            miniGame.identifier,
                            miniGame.released_at,
                            miniGame.in_out,
                            miniGame.image_url
                        )}
                    />
                    {miniGame.restricted && <SpriteIcon
                        name="#svg-traffic-barrier"
                        className="svg-default popular-games-carousel__miniature-barrier-icon"
                    />}
                    <span className="popular-games-carousel__miniature-name">{miniGame.name}</span>
                </button>
            );
        });
    };

    render(): React.ReactNode {
        const {games} = this;
        const {activeIndex} = this.state;
        const {cool_off_present} = this.props;
        const game = games[activeIndex] as (CasinoGameInterface & {payout?: string}) | undefined;

        if (games.length === 0 || !game) {
            return null;
        }

        const {payout} = game;
        const restricted = Boolean(game.restricted);
        const bigCardGameClass = `casino-game-item popular-games-carousel__big-card-game${restricted ? ' casino-game-item--restricted' : ''}`;

        return (
            <div className="popular-games-carousel">
                <div className="popular-games-carousel__big-card">
                    <div className="popular-games-carousel__big-card-image-wrap">
                        <div className={bigCardGameClass}>
                            <div className="casino-game-image">
                                <img
                                    key={`image-active-card-${activeIndex}`}
                                    alt={game.identifier2}
                                    src={size190x190(
                                        game.provider_name,
                                        game.identifier,
                                        game.released_at,
                                        game.in_out,
                                        game.image_url
                                    )}
                                />
                            </div>
                            {restricted && <div className="casino-game-restricted-overlay">
                                <SpriteIcon name="#svg-traffic-barrier" className="svg-default casino-game-restricted-overlay__icon"/>
                                <div className="casino-game-restricted-overlay__container">
                                    <SpriteIcon name="#svg-traffic-barrier" className="svg-default casino-game-restricted-overlay__container-icon"/>
                                    <span className="casino-game-restricted-overlay__text">
                                        {I18n.t('casino_slot_restricted_text')}
                                    </span>
                                    <button
                                        className="casino-game-restricted-overlay__details"
                                        onClick={this.openRestrictedPopup}
                                    >
                                        {I18n.t('casino_slot_restricted_details')}
                                    </button>
                                </div>
                            </div>}
                        </div>
                        <div key={`body-active-card-${activeIndex}`} className="popular-games-carousel__big-card-info">
                            <h3 className="popular-games-carousel__game-name">{game.name}</h3>
                            <p className="popular-games-carousel__provider">{game.provider_title || game.provider_name}</p>
                            <div className="popular-games-carousel__buttons">
                                {isGuest()
                                    ? <SignInButton title={I18n.t('play')} usingOnTab={'CASINO'}/>
                                    : <button
                                        type="button"
                                        disabled={cool_off_present}
                                        className="button-adaptive button-adaptive_casino popular-games-carousel__btn-play"
                                        onClick={this.handleStartGame}
                                    >
                                        {I18n.t('play')}
                                    </button>
                                }
                                {isGuest()
                                    ? <SignInButton
                                        usingOnTab={'CASINO'}
                                        className="button-adaptive_casino-icon"
                                    >
                                        <SpriteIcon name="#svg-button-eye" className="svg-default"/>
                                        <span className="button-adaptive__name">{I18n.t('demo')}</span>
                                    </SignInButton>
                                    : <button
                                        type="button"
                                        disabled={cool_off_present}
                                        className="button-adaptive_casino-icon"
                                        onClick={this.handleStartDemo}
                                    >
                                        <SpriteIcon name="#svg-button-eye" className="svg-default"/>
                                        <span className="button-adaptive__name">{I18n.t('demo')}</span>
                                    </button>
                                }
                            </div>
                        </div>
                        <div className="popular-games-carousel__progress">
                            {this.renderProgressSegments()}
                        </div>
                        <div className="popular-games-carousel__badges" key={`badges-active-card-${activeIndex}`}>
                            {payout?.length > 0 &&
                                <span className="casino-game-rtp popular-games-carousel__rtp">
                                    {payout}%
                                </span>
                            }
                            {game.points &&
                                <span className="casino-game-points__label popular-games-carousel__points">
                                    + {I18n.t('user.points')}
                                </span>
                            }
                        </div>
                        <button
                            type="button"
                            className="popular-games-carousel__arrow popular-games-carousel__arrow_prev"
                            onClick={this.goToPrev}
                            aria-label={I18n.t('will_paginate.previous_label')}
                        >
                            <SpriteIcon name="#pag-arr-left" className="svg-default"/>
                        </button>
                        <button
                            type="button"
                            className="popular-games-carousel__arrow popular-games-carousel__arrow_next"
                            onClick={this.handleNextClick}
                            aria-label={I18n.t('will_paginate.next_label')}
                        >
                            <SpriteIcon name="#pag-arr-right" className="svg-default"/>
                        </button>
                    </div>
                </div>
                <div className="popular-games-carousel__miniatures">
                    {this.renderMiniatures()}
                </div>
            </div>
        );
    }
}
